// Word-level diff, ported from the design's wordDiff(). Used to render the
// reframe-vs-as-read tracked changes in P7 (the P5 neutral diff uses the bundle's
// PRECOMPUTED diff_ops instead — we never recompute that one in the browser).

import type { DiffOp } from "./types";

function tokenize(s: string): string[] {
  return s.match(/\s+|\S+/g) || [];
}

export function wordDiff(aStr: string, bStr: string): DiffOp[] {
  const a = tokenize(aStr), b = tokenize(bStr), n = a.length, m = b.length;
  // LCS DP
  const dp: Int32Array[] = Array.from({ length: n + 1 }, () => new Int32Array(m + 1));
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--)
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const ops: DiffOp[] = [];
  const push = (op: DiffOp["op"], text: string) => {
    const last = ops[ops.length - 1];
    if (last && last.op === op) last.text += text;
    else ops.push({ op, text } as DiffOp);
  };
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { push("equal", a[i]); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { push("delete", a[i]); i++; }
    else { push("insert", b[j]); j++; }
  }
  while (i < n) { push("delete", a[i]); i++; }
  while (j < m) { push("insert", b[j]); j++; }
  return ops;
}

export interface DiffPara { ops: DiffOpView[]; key: number }
export interface DiffOpView {
  isEqual: boolean;
  isChange: boolean;
  opType: DiffOp["op"];
  text: string;
  change_ref?: number;
}

/** Split a flat op list into paragraphs on the "\n\n" boundaries inside equal runs. */
export function splitParas(
  list: DiffOp[],
  keep: (op: DiffOp) => boolean = () => true
): DiffPara[] {
  const out: DiffOpView[][] = [[]];
  list.forEach((op) => {
    if (op.op === "equal") {
      const parts = op.text.split("\n\n");
      parts.forEach((pt, idx) => {
        if (idx > 0) out.push([]);
        if (pt) out[out.length - 1].push({ isEqual: true, isChange: false, opType: "equal", text: pt });
      });
    } else if (keep(op)) {
      out[out.length - 1].push({ isEqual: false, isChange: true, opType: op.op, text: op.text, change_ref: (op as any).change_ref });
    }
  });
  return out.filter((p) => p.length).map((o, i) => ({ ops: o, key: i }));
}
